import type { GeometryType, LegendPaint } from '../api/types';

export type SwatchShape = 'point' | 'line' | 'polygon';

export function swatchShape(geometry: GeometryType): SwatchShape {
  if (geometry.endsWith('Point')) return 'point';
  if (geometry.endsWith('LineString')) return 'line';
  return 'polygon';
}

export interface SwatchStyle {
  shape: SwatchShape;
  fill: string;
  fillOpacity: number;
  stroke: string;
  strokeWidth: number;
  radius: number;
  dash: string | undefined;
  /** A tile drawn under the symbol, when the symbol alone would vanish on the white legend (see SWATCH_BACKDROP). */
  backdrop: string | undefined;
}

/**
 * The tile under a symbol that is white or nearly white all over: a white boundary on a clear fill, a white road
 * string. Those symbols are drawn for the dark imagery they sit on, and on the legend's white (or the printed page's)
 * they vanish, leaving a row with no swatch at all. A neutral slate, so a white line still reads as a white line.
 */
export const SWATCH_BACKDROP = '#5b6672';

/** '#rgb' or '#rrggbb' as sRGB channels 0..1; null for anything else (no backdrop is guessed for it). */
function rgbOf(colour: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(colour.trim());
  if (!m) return null;
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
}

/** Light and colourless: what cannot be told from white paper. A light yellow still reads on white by its hue. */
function nearWhite([r, g, b]: readonly [number, number, number]): boolean {
  const lin = (v: number): number => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance >= 0.75 && Math.max(r, g, b) - Math.min(r, g, b) <= 0.12;
}

/** A colour at an opacity, as it shows over white. */
const overWhite = (c: readonly [number, number, number], a: number): [number, number, number] =>
  c.map((v) => 1 - a * (1 - v)) as [number, number, number];

/** Whether every part the swatch draws (its fill, as it shows over white, and its outline) would vanish on white. */
function vanishesOnWhite(s: Omit<SwatchStyle, 'backdrop'>): boolean {
  const parts: ([number, number, number] | null)[] = [];
  if (s.fill !== 'none') {
    const f = rgbOf(s.fill);
    parts.push(f && overWhite(f, Math.min(1, Math.max(0, s.fillOpacity))));
  }
  if (s.stroke !== 'none') parts.push(rgbOf(s.stroke));
  return parts.length > 0 && parts.every((c) => c !== null && nearWhite(c));
}

/** Resolve a legend paint to concrete SVG attributes for a 16px swatch. */
export function swatchStyle(geometry: GeometryType, paint: LegendPaint): SwatchStyle {
  const shape = swatchShape(geometry);
  const fallback = '#888888';
  const style = {
    shape,
    fill: shape === 'line' ? 'none' : (paint.fill ?? fallback),
    fillOpacity: paint.fillOpacity ?? 1,
    stroke: paint.stroke ?? (shape === 'line' ? (paint.fill ?? fallback) : 'none'),
    strokeWidth: Math.min(paint.strokeWidth ?? (shape === 'line' ? 2 : 1), 4),
    radius: Math.min(paint.radius ?? 4, 7),
    dash: paint.dash && paint.dash.length > 0 ? paint.dash.join(' ') : undefined,
  };
  return { ...style, backdrop: vanishesOnWhite(style) ? SWATCH_BACKDROP : undefined };
}
